/**
 * useOfflineSync — monitors online/offline state, shows the pending
 * queue count, and replays mutations when reconnected.
 */
import { useState, useEffect, useCallback } from 'react';
import { getPendingCount, getPendingMutations, markMutationDone, markMutationFailed } from '../services/offlineQueue';
import api from '../services/api';

export function useOfflineSync() {
  const [isOnline, setIsOnline]     = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing]       = useState(false);

  const refreshCount = useCallback(async () => {
    const n = await getPendingCount();
    setPendingCount(n);
  }, []);

  // Replay the offline mutation queue
  const replayQueue = useCallback(async () => {
    const pending = await getPendingMutations();
    if (!pending.length) return;

    setSyncing(true);
    let failed = 0;

    for (const mutation of pending) {
      try {
        await api.request({
          method: mutation.method,
          url:    mutation.url,
          data:   mutation.body,
          headers: {
            'X-Client-Timestamp': mutation.client_timestamp,
            'X-Offline-Replay':   'true',
          },
        });
        await markMutationDone(mutation.id);
      } catch (err) {
        await markMutationFailed(mutation.id, err?.message || 'failed');
        failed++;
      }
    }

    await refreshCount();
    setSyncing(false);
    return { replayed: pending.length, failed };
  }, [refreshCount]);

  useEffect(() => {
    const goOnline = async () => {
      setIsOnline(true);
      await replayQueue();
    };
    const goOffline = () => setIsOnline(false);

    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);

    // Listen for SW message to replay queue (Background Sync via SW)
    window.addEventListener('pwa-replay-queue', replayQueue);

    refreshCount();

    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('pwa-replay-queue', replayQueue);
    };
  }, [replayQueue, refreshCount]);

  return { isOnline, pendingCount, syncing, replayQueue, refreshCount };
}
